import { useState, useEffect, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { useAuthStore, type AuthUser } from '../store/authStore'
import { useTheme } from '../hooks/useTheme'

const SIM_API = import.meta.env.VITE_SIM_API_URL || 'http://localhost:8001'

interface UserRecord {
  username: string
  role: 'admin' | 'viewer'
  full_name: string
  active: boolean
}

function authHeaders(token: string) {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
}

export default function AdminUsers({ onClose }: { onClose: () => void }) {
  const C = useTheme()
  const { token, user: me } = useAuthStore()

  const [users,    setUsers]    = useState<UserRecord[]>([])
  const [loading,  setLoading]  = useState(true)
  const [error,    setError]    = useState<string | null>(null)
  const [success,  setSuccess]  = useState<string | null>(null)

  // New user form
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'viewer', full_name: '' })
  const [creating, setCreating] = useState(false)

  // Change password
  const [pwTarget, setPwTarget]   = useState<string | null>(null)
  const [newPw,    setNewPw]      = useState('')
  const [pwLoading, setPwLoading] = useState(false)

  async function fetchUsers() {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`${SIM_API}/api/v1/auth/users`, {
        headers: authHeaders(token!),
      })
      if (!res.ok) throw new Error(`Error ${res.status}`)
      setUsers(await res.json())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error cargando usuarios')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchUsers() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleCreate(e: FormEvent) {
    e.preventDefault()
    setCreating(true)
    setError(null)
    setSuccess(null)
    try {
      const res = await fetch(`${SIM_API}/api/v1/auth/users`, {
        method: 'POST',
        headers: authHeaders(token!),
        body: JSON.stringify(newUser),
      })
      if (!res.ok) {
        const d = await res.json().catch(() => ({}))
        throw new Error(d.detail ?? `Error ${res.status}`)
      }
      setNewUser({ username: '', password: '', role: 'viewer', full_name: '' })
      setSuccess('Usuario creado correctamente.')
      await fetchUsers()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error creando usuario')
    } finally {
      setCreating(false)
    }
  }

  async function handleDelete(username: string) {
    if (!confirm(`¿Eliminar usuario "${username}"?`)) return
    setError(null)
    setSuccess(null)
    try {
      const res = await fetch(`${SIM_API}/api/v1/auth/users/${username}`, {
        method: 'DELETE',
        headers: authHeaders(token!),
      })
      if (!res.ok) {
        const d = await res.json().catch(() => ({}))
        throw new Error(d.detail ?? `Error ${res.status}`)
      }
      setSuccess(`Usuario "${username}" eliminado.`)
      await fetchUsers()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error eliminando usuario')
    }
  }

  async function handleChangePassword(e: FormEvent) {
    e.preventDefault()
    if (!pwTarget) return
    setPwLoading(true)
    setError(null)
    setSuccess(null)
    try {
      const res = await fetch(`${SIM_API}/api/v1/auth/users/${pwTarget}/password`, {
        method: 'PATCH',
        headers: authHeaders(token!),
        body: JSON.stringify({ new_password: newPw }),
      })
      if (!res.ok) {
        const d = await res.json().catch(() => ({}))
        throw new Error(d.detail ?? `Error ${res.status}`)
      }
      setSuccess(`Contraseña de "${pwTarget}" actualizada.`)
      setPwTarget(null)
      setNewPw('')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error cambiando contraseña')
    } finally {
      setPwLoading(false)
    }
  }

  const modal = (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: 'fixed', inset: 0, background: C.overlay,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: 20,
      }}
    >
      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: 14, width: '100%', maxWidth: 680,
        maxHeight: '85vh', overflow: 'auto',
        boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
        fontFamily: "'Open Sans', system-ui, sans-serif",
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 24px', borderBottom: `1px solid ${C.border}`,
          position: 'sticky', top: 0, background: C.card, zIndex: 1,
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>
              👤 Gestión de Usuarios
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
              Solo administradores pueden gestionar accesos
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: C.muted,
            fontSize: 20, cursor: 'pointer', padding: '4px 8px',
          }}>×</button>
        </div>

        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 24 }}>

          {/* Feedback */}
          {error && (
            <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#ef4444' }}>
              {error}
            </div>
          )}
          {success && (
            <div style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.35)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: C.green }}>
              {success}
            </div>
          )}

          {/* User list */}
          <section>
            <div style={{ fontSize: 11, color: C.blue, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 12 }}>
              Usuarios Activos
            </div>
            {loading ? (
              <div style={{ fontSize: 12, color: C.muted, padding: '12px 0' }}>Cargando…</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {users.map((u) => (
                  <div key={u.username} style={{
                    background: C.card2, border: `1px solid ${C.border}`,
                    borderRadius: 8, padding: '10px 14px',
                    display: 'flex', alignItems: 'center', gap: 12,
                  }}>
                    <div style={{
                      width: 34, height: 34, borderRadius: '50%',
                      background: u.role === 'admin' ? 'rgba(248,148,33,0.2)' : 'rgba(2,175,240,0.15)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 16, flexShrink: 0,
                    }}>
                      {u.role === 'admin' ? '👑' : '👤'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: C.text }}>{u.full_name || u.username}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>@{u.username}</div>
                    </div>
                    <span style={{
                      padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700,
                      background: u.role === 'admin' ? 'rgba(248,148,33,0.15)' : 'rgba(2,175,240,0.12)',
                      color: u.role === 'admin' ? C.orange : C.blue,
                    }}>
                      {u.role.toUpperCase()}
                    </span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => { setPwTarget(u.username); setNewPw('') }}
                        style={{ background: 'rgba(255,255,255,0.07)', border: `1px solid ${C.border}`, color: C.muted, borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}
                      >
                        Contraseña
                      </button>
                      {u.username !== me?.username && (
                        <button
                          onClick={() => handleDelete(u.username)}
                          style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}
                        >
                          Eliminar
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Change password inline form */}
          {pwTarget && (
            <section style={{ background: 'rgba(2,175,240,0.06)', border: `1px solid rgba(2,175,240,0.2)`, borderRadius: 10, padding: '16px 18px' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.blue, marginBottom: 12 }}>
                Cambiar contraseña — @{pwTarget}
              </div>
              <form onSubmit={handleChangePassword} style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 10, color: C.muted, display: 'block', marginBottom: 4 }}>Nueva contraseña (mín. 6 chars)</label>
                  <input
                    type="password"
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    required minLength={6}
                    style={{ width: '100%', boxSizing: 'border-box', background: C.card, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 10px', fontSize: 12, fontFamily: 'inherit' }}
                  />
                </div>
                <button type="submit" disabled={pwLoading} style={{ background: '#02AFF0', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                  {pwLoading ? '…' : 'Guardar'}
                </button>
                <button type="button" onClick={() => setPwTarget(null)} style={{ background: 'none', border: `1px solid ${C.border}`, color: C.muted, borderRadius: 6, padding: '8px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Cancelar
                </button>
              </form>
            </section>
          )}

          {/* Create user form */}
          <section>
            <div style={{ fontSize: 11, color: C.blue, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 12 }}>
              Crear Nuevo Usuario
            </div>
            <form onSubmit={handleCreate} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[
                { key: 'username',  label: 'Usuario',       type: 'text',     placeholder: 'Ej. jperez' },
                { key: 'full_name', label: 'Nombre',        type: 'text',     placeholder: 'Juan Pérez' },
                { key: 'password',  label: 'Contraseña',    type: 'password', placeholder: '••••••••' },
              ].map(({ key, label, type, placeholder }) => (
                <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</label>
                  <input
                    type={type}
                    value={(newUser as Record<string,string>)[key]}
                    onChange={(e) => setNewUser((p) => ({ ...p, [key]: e.target.value }))}
                    placeholder={placeholder}
                    required={key !== 'full_name'}
                    minLength={key === 'password' ? 6 : undefined}
                    style={{ background: C.card2, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 10px', fontSize: 12, fontFamily: 'inherit' }}
                  />
                </div>
              ))}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Rol</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser((p) => ({ ...p, role: e.target.value }))}
                  style={{ background: C.card2, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 10px', fontSize: 12, fontFamily: 'inherit' }}
                >
                  <option value="viewer">Viewer — Solo lectura</option>
                  <option value="admin">Admin — Acceso total</option>
                </select>
              </div>

              <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="submit"
                  disabled={creating}
                  style={{
                    background: creating ? 'rgba(34,197,94,0.4)' : C.green,
                    color: '#fff', border: 'none', borderRadius: 8,
                    padding: '10px 24px', fontSize: 13, fontWeight: 700,
                    cursor: creating ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                  }}
                >
                  {creating ? 'Creando…' : '+ Crear Usuario'}
                </button>
              </div>
            </form>
          </section>

        </div>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}
